import { useEffect, useRef } from 'react';

// ─── Botão voltar fecha overlays ─────────────────────────────────────────────
// No Android/PWA o botão voltar é o gesto de cancelar mais usado. Para que ele
// feche uma gaveta/dialog em vez de sair da tela, cada overlay empilha uma
// entrada no histórico e reage ao `popstate`.
//
// Regras (ver docs/MOBILE.md):
// - UM listener global e UMA pilha: o voltar fecha sempre o overlay do topo;
// - UM ciclo por abertura (token): re-mounts do StrictMode não empilham duas
//   entradas nem fecham o ciclo recém-aberto;
// - `popstate` **sem travessia** do histórico (o documento continua na entrada
//   do próprio overlay) é evento extra — sintético, StrictMode, router — e não
//   fecha nada;
// - O pop do nosso próprio `back()` é reconhecido por **contagem por ciclo**
//   (nada de janela de tempo): se o usuário reabrir o overlay antes de ele
//   chegar, a gaveta recém-aberta sobrevive;
// - Se esse `back()` derrubou a entrada de um overlay aberto nesse meio-tempo,
//   ela é re-empilhada (`reancorarTopo`) para o voltar continuar fechando;
// - Ao fechar por botão/swipe a entrada empilhada é removida em um microtask,
//   que é cancelado se o efeito remontar antes (StrictMode) ou se outro ciclo
//   abrir no intervalo;
// - Overlay dirigido pela URL (`usarHistoricoDoRouter`) NÃO mexe no histórico:
//   o router já é a fonte de verdade.

const CHAVE_ESTADO = 'zoomproOverlay';

/** Handlers dos overlays abertos, do mais antigo ao mais recente. */
const pilha = [];
let listenerAtivo = false;
/** Pops que nós mesmos disparamos e ainda não chegaram (um por ciclo fechado). */
let popsProgramaticos = 0;
/** Ciclos abertos desde o boot — detecta reabertura no meio de um fechamento. */
let aberturas = 0;
/**
 * Assinatura do estado da entrada atual do histórico.
 *
 * Cada entrada tem o seu próprio estado. Comparar a assinatura é o que separa
 * "o usuário apertou voltar" — o estado muda para o da entrada de baixo — de
 * "chegou um `popstate` extra" (estado inalterado). Identidade de objeto não
 * serve: o navegador re-desserializa `history.state` a cada leitura.
 */
let assinaturaConhecida = 'null';

const assinaturaDoEstado = () => {
  if (typeof window === 'undefined') return 'null';

  try {
    return JSON.stringify(window.history.state ?? null);
  } catch (error) {
    // Estado não serializável (não deveria acontecer: `pushState` clona): sem
    // assinatura, qualquer pop é tratado como travessia.
    return '?';
  }
};

const tokenDaEntrada = () =>
  typeof window === 'undefined' ? undefined : window.history.state?.[CHAVE_ESTADO];

const urlAtual = () =>
  typeof window === 'undefined' ? '' : `${window.location.pathname}${window.location.search}`;

const novoToken = () => `ov-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Garante que o overlay do topo tenha uma entrada própria no histórico.
 *
 * O `history.back()` de um fechamento anterior pode cair depois de a gaveta
 * reabrir e derrubar a entrada nova. Sem reancorar, o voltar do usuário sairia
 * da tela em vez de fechar o overlay.
 */
const reancorarTopo = () => {
  const topo = pilha[pilha.length - 1];
  if (!topo || typeof window === 'undefined') return;
  if (tokenDaEntrada() === topo.ciclo.token) return;

  const token = novoToken();

  try {
    window.history.pushState({ ...window.history.state, [CHAVE_ESTADO]: token }, '');
  } catch (error) {
    // Contexto sem histórico (iframe restrito): o overlay fecha pelo botão.
    return;
  }

  topo.ciclo.token = token;
  assinaturaConhecida = assinaturaDoEstado();
};

const aoVoltar = () => {
  const assinatura = assinaturaDoEstado();
  const houveTravessia = assinatura !== assinaturaConhecida;
  assinaturaConhecida = assinatura;

  // 1) `popstate` sem travessia: evento extra (sintético/StrictMode). Fechar
  //    aqui era o que engolia o toque no sino logo depois de um swipe.
  if (!houveTravessia) return;

  // 2) Pop do nosso próprio `back()`: consome a expectativa do ciclo e devolve
  //    a entrada ao overlay que estiver aberto.
  if (popsProgramaticos > 0) {
    popsProgramaticos -= 1;
    reancorarTopo();
    return;
  }

  // 3) Voltar de verdade: fecha o overlay do topo.
  const topo = pilha[pilha.length - 1];
  if (!topo) return;

  pilha.pop();
  topo.fechar();
};

const garantirListener = () => {
  if (listenerAtivo || typeof window === 'undefined') return;
  window.addEventListener('popstate', aoVoltar);
  listenerAtivo = true;
};

const removerListener = () => {
  if (!listenerAtivo || typeof window === 'undefined') return;
  window.removeEventListener('popstate', aoVoltar);
  listenerAtivo = false;
};

/** Reinicia o estado do módulo. Existe apenas para testes. */
export const resetarHistoricoOverlays = () => {
  pilha.length = 0;
  popsProgramaticos = 0;
  aberturas = 0;
  assinaturaConhecida = assinaturaDoEstado();
  removerListener();
};

/**
 * Fecha o overlay atual quando o usuário aperta o botão voltar do celular.
 *
 * @param {boolean} aberto Estado de abertura do overlay.
 * @param {() => void} onFechar Callback que fecha o overlay.
 * @param {{usarHistoricoDoRouter?: boolean}} [opcoes] Use
 *   `usarHistoricoDoRouter` em overlays dirigidos pela URL (`?param`): nesse
 *   caso o hook não empilha nem remove nada, porque o router já faz isso.
 */
export const useFecharNoVoltar = (aberto, onFechar, { usarHistoricoDoRouter = false } = {}) => {
  const fecharRef = useRef(onFechar);
  const cicloRef = useRef(null);
  const cancelarFechamentoRef = useRef(null);

  useEffect(() => {
    fecharRef.current = onFechar;
  }, [onFechar]);

  useEffect(() => {
    if (!aberto || usarHistoricoDoRouter || typeof window === 'undefined') return undefined;

    // Re-mount imediato (StrictMode): o ciclo aberto continua valendo.
    if (cancelarFechamentoRef.current) {
      cancelarFechamentoRef.current();
      cancelarFechamentoRef.current = null;
    }

    if (!cicloRef.current) {
      const ciclo = { token: novoToken(), url: urlAtual() };
      cicloRef.current = ciclo;
      aberturas += 1;

      try {
        window.history.pushState({ ...window.history.state, [CHAVE_ESTADO]: ciclo.token }, '');
        assinaturaConhecida = assinaturaDoEstado();
      } catch (error) {
        // Contexto sem histórico (iframe restrito): o overlay fecha pelo botão.
      }
    }

    const ciclo = cicloRef.current;
    const entrada = { ciclo, fechar: () => fecharRef.current?.() };
    pilha.push(entrada);
    garantirListener();

    return () => {
      const indice = pilha.lastIndexOf(entrada);
      // Já consumida pelo `popstate`: o voltar já fechou este overlay.
      const consumidaPeloVoltar = indice === -1;
      if (!consumidaPeloVoltar) pilha.splice(indice, 1);

      if (consumidaPeloVoltar || !cicloRef.current) {
        cicloRef.current = null;
        return;
      }

      const aberturasNaSaida = aberturas;
      let cancelado = false;
      const cancelar = () => {
        cancelado = true;
      };
      cancelarFechamentoRef.current = cancelar;

      // `Promise.resolve().then` (e não `queueMicrotask`): roda no próximo
      // microtask, é cancelável pelo re-mount do StrictMode e não é afetado
      // por fake timers nos testes.
      Promise.resolve().then(() => {
        if (cancelarFechamentoRef.current === cancelar) cancelarFechamentoRef.current = null;
        if (cancelado) return;

        cicloRef.current = null;

        // Overlay navegou (ex.: abrir uma notificação): desfazer o histórico
        // cancelaria a navegação.
        if (urlAtual() !== ciclo.url) return;
        // A entrada já saiu da pilha do navegador por outro caminho.
        if (tokenDaEntrada() !== ciclo.token) return;
        // Outro overlay abriu depois deste fechamento: o `back()` agora
        // derrubaria a entrada dele, e o toque seguinte no sino se perderia.
        if (aberturas !== aberturasNaSaida) return;

        popsProgramaticos += 1;
        window.history.back();
      });
    };
  }, [aberto, usarHistoricoDoRouter]);
};

export default useFecharNoVoltar;
